/**
 * The StoryPlayerConditionSolver handles all condition variable setting. It listens to the IStoryPlayer and keeps
 * all the variables for conditions under the framework convention. To the outer world it provides a simple solve function
 * that resolves all the known variables and passes all other variables to a supplied solve function.
 * 
 * NOTE: at the moment the StoryPlayerConditionSolver doesn't look for changes in the story model, so if a node/arc is
 * added or removed during playback it can give unexpected results. This can be added quite easily.
 * 
 * TODO: this class should be internal but we want to test it with unit test, look for a good solution for this
 * 
 * @private
 * @constructor
 * @param {StoryPlayer} storyPlayer The engine StoryPlayer to manage conditions for
 * @param {function} solver Solver funciton to use
 * @param {IClock} clock The Clock to use
 **/

import { isUndefined } from "../../../../util/TypeUtil";
import StoryPlayerEvent from "../../engine/player/StoryPlayerEvent";
import StoryVariables from "./StoryVariables";

export default class StoryPlayerConditionSolver {
    constructor(storyPlayer, solver, clock) {
        this._conditions = {};
        this._solver = solver;
        this._clock = clock;
        this.setStoryPlayer(storyPlayer);
    }

    /**
     * @private
     * @returns {undefined}
     */
    addEventListeners() {
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_START, this.onStoryStart, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_AVAILABLE, this.onStoryArcAvailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_UNAVAILABLE, this.onStoryArcUnavailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_UNLOCKED, this.onStoryArcUnlocked, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_COMPLETED, this.onStoryArcCompleted, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_AVAILABLE, this.onStoryNodeAvailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_UNAVAILABLE, this.onStoryNodeUnavailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_UNLOCKED, this.onStoryNodeUnlocked, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_COMPLETED, this.onStoryNodeCompleted, this);
    }

    /**
     * @private
     * @returns {undefined}
     */
    removeEventListeners() {
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_START, this.onStoryStart, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_AVAILABLE, this.onStoryArcAvailable, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_UNAVAILABLE, this.onStoryArcUnavailable, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_UNLOCKED, this.onStoryArcUnlocked, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_COMPLETED, this.onStoryArcCompleted, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_AVAILABLE, this.onStoryNodeAvailable, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_UNAVAILABLE, this.onStoryNodeUnavailable, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_UNLOCKED, this.onStoryNodeUnlocked, this);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_COMPLETED, this.onStoryNodeCompleted, this);
    }

    /**
     * Cleanup the StoryPlayerConditionSolver
     * @returns {undefined}
     */
    destroy() {
        this.removeEventListeners();
        this._storyPlayer = null;
        this._solver = null;
        this._conditions = null;
    }

    /**
     * @private
     * @returns {undefined}
     */
    reset() {
        const story = this._storyPlayer.getStory();
        this._conditions[StoryVariables.storyStartTimeVariable()] = this._storyPlayer.getStoryStartTime();

        const storyArcItr = story.getStoryArcs();
        while (storyArcItr.hasNext()) {
            const storyArc = storyArcItr.next();
            //Set storyArc conditions
            this._conditions[StoryVariables.storyArcAvailableVariable(storyArc)] = this._storyPlayer.isStoryArcAvailable(storyArc);
            this._conditions[StoryVariables.storyArcAvailableTimeVariable(storyArc)] = this._storyPlayer.storyArcAvailableTime(storyArc);
            this._conditions[StoryVariables.storyArcUnlockedVariable(storyArc)] = this._storyPlayer.isStoryArcUnlocked(storyArc);
            this._conditions[StoryVariables.storyArcUnlockedTimeVariable(storyArc)] = this._storyPlayer.storyArcUnlockedTime(storyArc);
            this._conditions[StoryVariables.storyArcCompletedVariable(storyArc)] = this._storyPlayer.isStoryArcCompleted(storyArc);
            this._conditions[StoryVariables.storyArcCompletedTimeVariable(storyArc)] = this._storyPlayer.storyArcCompletedTime(storyArc);
            //Reset story nodes
            const storyNodeItr = storyArc.getStoryNodes();
            while (storyNodeItr.hasNext()) {
                const storyNode = storyNodeItr.next();
                //Set the storynode conditions
                this._conditions[StoryVariables.storyNodeUnlockedVariable(storyNode)] = this._storyPlayer.isStoryNodeUnlocked(storyNode);
                this._conditions[StoryVariables.storyNodeUnlockedTimeVariable(storyNode)] = this._storyPlayer.storyNodeUnlockedTime(storyNode);
                this._conditions[StoryVariables.storyNodeAvailableVariable(storyNode)] = this._storyPlayer.isStoryNodeAvailable(storyNode);
                this._conditions[StoryVariables.storyNodeAvailableTimeVariable(storyNode)] = this._storyPlayer.storyNodeAvailableTime(storyNode);
                this._conditions[StoryVariables.storyNodeCompletedVariable(storyNode)] = this._storyPlayer.isStoryNodeCompleted(storyNode);
                this._conditions[StoryVariables.storyNodeCompletedTimeVariable(storyNode)] = this._storyPlayer.storyNodeCompletedTime(storyNode);
            }
        }
    }

    /**
     * Set the StoryPlayer for this StoryPlayerConditionSolver
     * @param {StoryPlayer} val The StoryPlayer
     * @returns {undefined}
     */
    setStoryPlayer(val) {
        if (this._storyPlayer != null) {
            this.removeEventListeners();
            this._storyPlayer = null;
        }
        this._storyPlayer = val;
        this.reset();
        this.addEventListeners();
    }

    /**
     * Get the StoryPlayer for this StoryPlayerConditionSolver
     * @returns {StoryPlayer}
     */
    getStoryPlayer() {
        return this._storyPlayer;
    }

    /**
     * Set the solver function to use in case an expression cannot be solved by the 
     * StoryPlayerConditionSolver itself
     * @param {function} val The solver function to use
     * @returns {undefined}
     */
    setSolver(val) {
        this._solver = val;
    }

    /**
     * Get the solver function used
     * @returns {function}
     */
    getSolver() {
        return this._solver;
    }

    /**
     * Solves a variable
     * @param {String} input
     * @returns {*} Value of the expression
     */
    solve(input) {
        //TODO: if the input has the right syntax but the id does not excist we probably want to give a warning
        if (!isUndefined(this._conditions[input])) {
            return this._conditions[input];
        } else if (input === StoryVariables.currentTimeVariable()) {
            return this._clock.getCurrentTime();
        }
        return this._solver(input);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryStart(e) {
        this._conditions[StoryVariables.storyStartTimeVariable()] = this._storyPlayer.getStoryStartTime();
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcAvailable({ data }) {
        this._conditions[StoryVariables.storyArcAvailableVariable(data.storyArc)] = true;
        this._conditions[StoryVariables.storyArcAvailableTimeVariable(data.storyArc)] = this._storyPlayer.storyArcAvailableTime(data.storyArc);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcUnavailable({ data }) {
        this._conditions[StoryVariables.storyArcAvailableVariable(data.storyArc)] = false;
        this._conditions[StoryVariables.storyArcAvailableTimeVariable(data.storyArc)] = this._storyPlayer.storyArcAvailableTime(data.storyArc);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcUnlocked({ data }) {
        this._conditions[StoryVariables.storyArcUnlockedVariable(data.storyArc)] = true;
        this._conditions[StoryVariables.storyArcUnlockedTimeVariable(data.storyArc)] = this._storyPlayer.storyArcUnlockedTime(data.storyArc);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcCompleted({ data }) {
        this._conditions[StoryVariables.storyArcCompletedVariable(data.storyArc)] = true;
        this._conditions[StoryVariables.storyArcCompletedTimeVariable(data.storyArc)] = this._storyPlayer.storyArcCompletedTime(data.storyArc);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeUnlocked({ data }) {
        this._conditions[StoryVariables.storyNodeUnlockedVariable(data.storyNode)] = true;
        this._conditions[StoryVariables.storyNodeUnlockedTimeVariable(data.storyNode)] = this._storyPlayer.storyNodeUnlockedTime(data.storyNode);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeAvailable({ data }) {
        this._conditions[StoryVariables.storyNodeAvailableVariable(data.storyNode)] = true;
        this._conditions[StoryVariables.storyNodeAvailableTimeVariable(data.storyNode)] = this._storyPlayer.storyNodeAvailableTime(data.storyNode);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeUnavailable({ data }) {
        this._conditions[StoryVariables.storyNodeAvailableVariable(data.storyNode)] = false;
        this._conditions[StoryVariables.storyNodeAvailableTimeVariable(data.storyNode)] = this._storyPlayer.storyNodeAvailableTime(data.storyNode);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeCompleted({ data }) {
        this._conditions[StoryVariables.storyNodeCompletedVariable(data.storyNode)] = true;
        this._conditions[StoryVariables.storyNodeCompletedTimeVariable(data.storyNode)] = this._storyPlayer.storyNodeCompletedTime(data.storyNode);
    }
}
